/**
 * Convert number to words (supports INR lakhs/crores, USD thousands/millions)
 */

const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function convertHundreds(num: number): string {
  if (num === 0) return '';
  
  let result = '';

  if (num >= 100) {
    result += ones[Math.floor(num / 100)] + ' hundred ';
    num %= 100;
    if (num === 0) return result.trim();
  }

  if (num >= 20) {
    result += tens[Math.floor(num / 10)] + ' ';
    num %= 10;
  } else if (num >= 10) {
    result += teens[num - 10] + ' ';
    return result.trim();
  }

  if (num > 0) {
    result += ones[num] + ' ';
  }

  return result.trim();
}

function getCurrencyName(currencyCode: string): string {
  const names: Record<string, string> = {
    'USD': 'dollars',
    'EUR': 'euros',
    'GBP': 'pounds',
    'INR': 'rupees',
    'JPY': 'yen',
    'CNY': 'yuan',
    'AUD': 'Australian dollars',
    'CAD': 'Canadian dollars',
    'SGD': 'Singapore dollars',
    'MYR': 'Malaysian ringgit',
    'THB': 'Thai baht',
    'IDR': 'Indonesian rupiah',
    'PHP': 'Philippine pesos',
    'VND': 'Vietnamese dong',
    'HKD': 'Hong Kong dollars',
    'KRW': 'South Korean won',
    'NZD': 'New Zealand dollars',
    'CHF': 'Swiss francs',
    'MXN': 'Mexican pesos',
  };
  return names[currencyCode] || currencyCode.toLowerCase();
}

export const numberToWords = (num: number, currencyCode: string = 'INR'): string => {
  if (num === 0) {
    return currencyCode === 'INR' ? 'zero rupees' : `zero ${getCurrencyName(currencyCode)}`;
  }
  if (num < 0) return 'minus ' + numberToWords(-num, currencyCode);

  // For INR: Convert to lakhs/crores format
  if (currencyCode === 'INR') {
    // Helper function to convert remainder (< 100000) properly
    const convertRemainder = (remainder: number): string => {
      if (remainder === 0) return '';
      
      // Handle thousands (1000-99999)
      if (remainder >= 1000) {
        const thousands = Math.floor(remainder / 1000);
        const thousandRemainder = remainder % 1000;
        let result = convertHundreds(thousands) + ' thousand';
        if (thousandRemainder > 0) {
          const remainderWords = convertHundreds(thousandRemainder);
          if (remainderWords) {
            result += ' ' + remainderWords;
          }
        }
        return result;
      }
      
      // Handle hundreds and below (< 1000)
      return convertHundreds(remainder);
    };

    if (num >= 10000000) {
      const crores = Math.floor(num / 10000000);
      const remainder = num % 10000000;
      let result = convertHundreds(crores);
      if (result) {
        result += ' crore';
      } else {
        result = 'crore'; // Handle edge case where crores is 0 (shouldn't happen, but safe)
      }
      
      if (remainder >= 100000) {
        const lakhs = Math.floor(remainder / 100000);
        const lakhRemainder = remainder % 100000;
        const lakhWords = convertHundreds(lakhs);
        if (lakhWords) {
          result += ' ' + lakhWords + ' lakh';
        }
        const remainderWords = convertRemainder(lakhRemainder);
        if (remainderWords) {
          result += ' ' + remainderWords;
        }
      } else if (remainder > 0) {
        const remainderWords = convertRemainder(remainder);
        if (remainderWords) {
          result += ' ' + remainderWords;
        }
      }
      return result + ' rupees';
    } else if (num >= 100000) {
      const lakhs = Math.floor(num / 100000);
      const remainder = num % 100000;
      let result = convertHundreds(lakhs);
      if (result) {
        result += ' lakh';
      } else {
        result = 'lakh'; // Handle edge case
      }
      
      if (remainder > 0) {
        const remainderWords = convertRemainder(remainder);
        if (remainderWords) {
          result += ' ' + remainderWords;
        }
      }
      return result + ' rupees';
    } else if (num >= 1000) {
      // Handle thousands for INR (1000-99999)
      const thousands = Math.floor(num / 1000);
      const thousandRemainder = num % 1000;
      let result = convertHundreds(thousands) + ' thousand';
      if (thousandRemainder > 0) {
        const remainderWords = convertHundreds(thousandRemainder);
        if (remainderWords) {
          result += ' ' + remainderWords;
        }
      }
      return result + ' rupees';
    } else if (num > 0) {
      const words = convertHundreds(num);
      return words ? words + ' rupees' : 'zero rupees';
    } else {
      return 'zero rupees';
    }
  }

  // For other currencies: Use thousands/millions
  if (num >= 1000000) {
    const millions = Math.floor(num / 1000000);
    const remainder = num % 1000000;
    let result = convertHundreds(millions) + ' million';
    if (remainder >= 1000) {
      const thousands = Math.floor(remainder / 1000);
      const thousandRemainder = remainder % 1000;
      if (thousands > 0) {
        result += ' ' + convertHundreds(thousands) + ' thousand';
      }
      if (thousandRemainder > 0) {
        result += ' ' + convertHundreds(thousandRemainder);
      }
    } else if (remainder > 0) {
      result += ' ' + convertHundreds(remainder);
    }
    return result + ' ' + getCurrencyName(currencyCode);
  } else if (num >= 1000) {
    const thousands = Math.floor(num / 1000);
    const remainder = num % 1000;
    let result = convertHundreds(thousands) + ' thousand';
    if (remainder > 0) {
      result += ' ' + convertHundreds(remainder);
    }
    return result + ' ' + getCurrencyName(currencyCode);
  } else if (num > 0) {
    return convertHundreds(num) + ' ' + getCurrencyName(currencyCode);
  } else {
    return 'zero ' + getCurrencyName(currencyCode);
  }
};

